import React, { useState, useEffect } from "react";
import { getRateLimitStatus, RateLimitStatus } from "../lib/api";
import { Progress } from "./ui/progress";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertTitle, AlertDescription } from "./ui/alert";
import { ReloadIcon } from "@radix-ui/react-icons";
import { formatDistanceToNow } from "date-fns";

export function RateLimitMonitor() {
  const [status, setStatus] = useState<RateLimitStatus | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getRateLimitStatus();
      if (response.success && response.data) {
        setStatus(response.data);
        setLastUpdated(new Date());
      } else {
        setError(response.error || "Failed to fetch rate limit data");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchStatus();

    // Cleanup function will run when component unmounts
    return () => {
      if (refreshInterval !== null) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  const toggleAutoRefresh = () => {
    if (refreshInterval !== null) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    } else {
      const interval = window.setInterval(() => {
        fetchStatus();
      }, 10000); // Refresh every 10 seconds
      setRefreshInterval(interval);
    }
  };

  const getStatusColor = (percent: number) => {
    if (percent > 70) return "bg-green-500";
    if (percent > 30) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-lg font-bold">Dataverse API Rate Limit</CardTitle>
            <CardDescription>Monitor API usage and availability</CardDescription>
          </div>
          <Badge variant={refreshInterval !== null ? "default" : "outline"}>
            {refreshInterval !== null ? "Auto-refresh on" : "Auto-refresh off"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {status ? (
          <>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>Available tokens</span>
                <span className="font-mono">
                  {status.availableTokens} / {status.maxTokens}
                </span>
              </div>
              <Progress 
                value={status.percentAvailable} 
                className={getStatusColor(status.percentAvailable)}
                max={100} 
              />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Current rate</div>
                <div className="font-semibold">{status.currentRate} req/sec</div>
              </div>
              <div>
                <div className="text-gray-500">Max burst</div>
                <div className="font-semibold">{status.maxTokens} requests</div>
              </div>
            </div>
          </>
        ) : (
          <div className="h-20 flex items-center justify-center">
            {loading ? (
              <ReloadIcon className="h-6 w-6 animate-spin" />
            ) : (
              <span className="text-gray-500">No data available</span>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between pt-2 border-t">
        <div className="text-xs text-gray-500">
          {lastUpdated
            ? `Last updated ${formatDistanceToNow(lastUpdated)} ago`
            : "Never updated"}
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={toggleAutoRefresh} 
            variant="outline" 
            size="sm"
          >
            {refreshInterval !== null ? "Stop Auto-refresh" : "Auto-refresh"}
          </Button>
          <Button 
            onClick={fetchStatus} 
            size="sm"
            disabled={loading}
          >
            {loading ? (
              <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ReloadIcon className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}